<template>
  <div>
    <div class="answer-block">
      <VTextField
        v-model="currentAnswer.answer"
        required
        outlined
        dense
        type="number"
        class="answer-textfield"
        :hint="`${$t('charactersLimit')} - 255`"
        :counter="255"
        :placeholder="$t('enterTheCorrectAnswer')"
        :error-messages="answerErrors"
        @keydown.enter.prevent
        @input="saveAnswer"
      />
    </div>
  </div>
</template>

<script>
import {
  toRefs, ref, computed, onMounted, watch,
} from '@vue/composition-api';
import { useI18n } from 'vue-i18n-composable';

export default {
  name: 'TestQuestionNumericType',
  props: {
    answers: {
      type: Array,
      required: true,
    },
    showValidationMessagesGlobal: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { answers, showValidationMessagesGlobal } = toRefs(props);
    const { t } = useI18n();

    const currentAnswer = ref(answers.value[0]);

    const isEmptyAnswer = computed(() => !currentAnswer.value.answer || !currentAnswer.value.answer.trim());
    const isEmptyAnswerByUser = computed(() => currentAnswer.value.answer === '');
    const isAnswerTooLong = computed(() => currentAnswer?.value?.answer?.length > 255);
    const isNotNumeric = computed(() => currentAnswer?.value?.answer && Number.isNaN(currentAnswer?.value?.answer));

    const showValidationMessages = ref(false);

    const isValid = computed(() => {
      return !(isEmptyAnswer.value || isAnswerTooLong.value || isNotNumeric.value);
    });

    function saveAnswer(initial = false) {
      currentAnswer.value.isValid = isValid.value;

      if (initial === true) {
        emit('initialChangeAnswers', [currentAnswer.value]);
        return;
      }

      emit('changeAnswers', [currentAnswer.value]);
    }

    const answerErrors = computed(() => {
      const errors = [];

      if (isNotNumeric.value) {
        errors.push(t('enterANumValue'));
      }

      if (isEmptyAnswer.value && (showValidationMessages.value || isEmptyAnswerByUser.value)) {
        errors.push(t('requiredField'));
      }

      if (isAnswerTooLong.value) {
        errors.push(`${t('charactersLimit')} - ${255}`);
      }

      return errors;
    });

    watch(showValidationMessagesGlobal, (flag) => {
      if (!flag) {
        return;
      }

      showValidationMessages.value = true;
    });

    onMounted(() => {
      saveAnswer(true);
    });

    return {
      saveAnswer,
      answerErrors,
      currentAnswer,
      isNotNumeric,
    };
  },
};
</script>

<style lang="scss" scoped>
.answer-block {
  display: flex;
  width: 75%;
  @media screen and (max-width: 900px) {
    width: 100%;
  }
}
.answer-textfield {
  max-width: 350px;
  @media screen and (max-width: 599px) {
    max-width: 80%;
  }
}
</style>

<i18n locale="en" lang="yaml">
  charactersLimit: The maximum number of characters is
  enterTheCorrectAnswer: 'Enter the correct answer*'
  requiredField: This field is required
  enterANumValue: Enter a numeric value
</i18n>

<i18n locale="ru" lang="yaml">
  charactersLimit: Максимальное количество символов
  enterTheCorrectAnswer: 'Введите правильный ответ*'
  requiredField: Обязательное поле для заполнения
  enterANumValue: Введите числовое значение
</i18n>
